import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChangeLogDb } from '@app/core/db/change-log';
import { ChangeLogControllerService, ChangeLogDto, CommentControllerService, CommentDto } from '@app/generated';
import { UploadChangeAttachmentRequest } from '@app/generated/model/uploadChangeAttachmentRequest';
import { NetworkStatusService } from '@app/core/services/network-status.service';
import { ErrorDialogManagerService } from '@app/shared/errors/error-dialog-manager/error-dialog-manager.service';
import { Mutex } from 'async-mutex';
import {
  ErrorDialogComponent,
  ErrorDialogComponentModel,
} from '@app/shared/errors/error-dialog-manager/error-dialog/error-dialog.component';
import { BroadcastChannel as RxBroadcastChannel, createLeaderElection } from 'broadcast-channel';
import { LoaderService } from '@app/header/loader/loader.service';
import { Subject } from 'rxjs';

// Message contract for the broadcast-channel named 'dwo-sync'
export type SyncMessage =
  | { type: 'sync' }
  | { type: 'processed'; item: any } // shape of 'item' can be refined if you have a type
  | { type: 'synced'; refs: string[] };

export type ChangeSyncEvent =
  | { state: 'syncing'; refs: string[] }
  | { state: 'success'; refs: string[] }
  | { state: 'error'; refs: string[] };

type SyncAttemptResult = {
  success: boolean;
  retryable: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class ChangeSyncService {
  isRegistered = false;
  db: ChangeLogDb;
  private syncChannel: RxBroadcastChannel<SyncMessage>;
  syncLock: Mutex;
  private readonly maxImmediateRetries = 2;
  private readonly immediateRetryDelayMs = 600;
  private readonly verificationAttempts = 4;
  private readonly verificationRetryDelayMs = 400;
  private readonly delayedResyncMs = 5000;
  private syncScheduled = false;
  private readonly syncEventsSubject = new Subject<ChangeSyncEvent>();
  readonly syncEvents$ = this.syncEventsSubject.asObservable();
  private readonly commentChangeTypes = new Set(['WO_ADD_COMMENT', 'WO_UPDATE_COMMENT', 'WO_DELETE_COMMENT']);

  constructor(
    private changeLogController: ChangeLogControllerService,
    private commentControllerService: CommentControllerService,
    private networkService: NetworkStatusService,
    private errorDialogService: ErrorDialogManagerService,
    private loaderService: LoaderService,
  ) {
    this.db = new ChangeLogDb();
    this.syncLock = new Mutex();
    this.syncChannel = new RxBroadcastChannel<SyncMessage>('dwo-sync');
  }

  register(): void {
    if (this.isRegistered) {
      throw new Error('The Change Sync service can only be registered once');
    }

    this.isRegistered = true;
    const elector = createLeaderElection(this.syncChannel);

    elector.awaitLeadership().then(async () => {
      await this.requeueFailedChanges();
      await this.purgeOldChanges();

      this.syncChannel.onmessage = async (message) => {
        if (message.type === 'sync') {
          await this.syncChanges();
        }
      };

      this.networkService.isOnline().subscribe(async (online) => {
        if (!online) return;
        await this.syncChanges();
      });

      this.loaderService.backendReachableStream.subscribe(async (reachable) => {
        if (!reachable || !navigator.onLine) return;
        await this.syncChanges();
      });

      await this.syncChanges();
    });
  }

  private async syncChanges(): Promise<void> {
    if (!this.canRetryImmediately()) {
      return;
    }

    const releaseLock = await this.syncLock.acquire();
    const syncedRefs = new Set<string>();
    const attemptedRefs = new Set<string>();

    try {
      let hasQueuedItems = false;

      const items = await this.db.events.where('status').equals('PENDING').toArray();
      items.forEach((item) => item.refs.forEach((ref) => attemptedRefs.add(ref)));

      if (attemptedRefs.size > 0) {
        this.syncEventsSubject.next({ state: 'syncing', refs: [...attemptedRefs] });
      }

      for (const item of items) {
        if (item.id == null) {
          continue;
        }

        const itemId = item.id;
        let result: SyncAttemptResult = { success: false, retryable: true };
        let attempts = 0;
        const immediateRetryLimit = this.canRetryImmediately() ? this.maxImmediateRetries : 0;

        // If backend is unreachable, try once and wait for the reachability signal before retrying.
        while (attempts <= immediateRetryLimit && navigator.onLine && !result.success && result.retryable) {
          attempts += 1;
          result = await this.trySyncItem(item);

          if (!result.success && result.retryable && attempts <= immediateRetryLimit) {
            await this.delay(this.immediateRetryDelayMs);
          }
        }

        const time = Date.now();
        const retryCount = (item.retryCount ?? 0) + (result.success ? 0 : 1);
        const keepQueued = !result.success && result.retryable;
        const status = result.success ? 'SUCCESS' : result.retryable ? 'PENDING' : 'FAILED';
        const shouldLogBackoff = keepQueued && retryCount >= Math.max(1, immediateRetryLimit);
        hasQueuedItems = hasQueuedItems || keepQueued;

        // Keep failed items queued (PENDING) so they can be retried when online again.
        // Only clear attachments on SUCCESS so retries keep the blob available.
        await this.db.events.update(itemId, {
          status,
          updated: time,
          retryCount,
          attachments: result.success ? null : item.attachments,
        });

        if (!result.success && !result.retryable) {
          console.warn(`change sync: Event ${item.key} marked FAILED due to non-retryable backend response`);
        }

        if (shouldLogBackoff) {
          console.warn(`change sync: Event ${item.key} is still queued after ${retryCount} attempts`);
        }

        const updatedItem = await this.db.events.get(itemId);
        await this.syncChannel.postMessage({ type: 'processed', item: updatedItem });

        item.refs.forEach((ref) => syncedRefs.add(ref));
      }

      if (hasQueuedItems && this.canRetryImmediately()) {
        this.scheduleDelayedSync();
      }

      await this.syncChannel.postMessage({ type: 'synced', refs: [...syncedRefs] });
      if (attemptedRefs.size > 0 && !hasQueuedItems) {
        this.syncEventsSubject.next({ state: 'success', refs: [...syncedRefs] });
      } else if (hasQueuedItems) {
        this.syncEventsSubject.next({ state: 'error', refs: [...attemptedRefs] });
      }
    } catch (e) {
      const syncError = e instanceof Error ? e : new Error(String(e));
      console.warn('change sync: Failed. Aborting!');
      console.warn(syncError);
      if (attemptedRefs.size > 0) {
        this.syncEventsSubject.next({ state: 'error', refs: [...attemptedRefs] });
      }
      if (localStorage.getItem('show_error_dialog')) {
        this.errorDialogService.error(ErrorDialogComponent, {
          title: 'error.dialog.general_error.title',
          description: 'error.dialog.general_error.description',
          button: 'error.dialog.general_error.button',
          message: `There was an error while trying to sync the changes. ${syncError.message}`,
        } as ErrorDialogComponentModel);
      }
    } finally {
      releaseLock();
    }
  }

  private async purgeOldChanges(): Promise<void> {
    await this.db.events.where('status').equals('SUCCESS').delete();
  }

  private async requeueFailedChanges(): Promise<void> {
    await this.db.events.where('status').equals('FAILED').modify({ status: 'PENDING' });
  }

  private async trySyncItem(item: any): Promise<SyncAttemptResult> {
    const payload: any = item.content;
    const workOrderId: string = payload?.workOrderId;
    const commentId: string = payload?.id;

    // 1) Apply metadata through changelog endpoint
    const request: ChangeLogDto = {
      id: item.key,
      type: item.type,
      refs: item.refs,
      content: item.content,
    };

    let metaOk = false;
    try {
      const response = await this.changeLogController.applyChange(request, 'response').toPromise();
      metaOk = !!response?.ok;
    } catch (error) {
      return { success: false, retryable: this.isRetryableSyncError(error) };
    }

    if (!metaOk) {
      return { success: false, retryable: true };
    }

    // 2) For queued comment changes, verify backend state before marking success.
    const verified = await this.verifyCommentChange(item.type, workOrderId, commentId);
    if (!verified) {
      return { success: false, retryable: true };
    }

    // 3) Upload attachments (if any) after metadata+verification
    if (item.attachments && item.attachments.length > 0) {
      try {
        for (const attachment of item.attachments) {
          if (!attachment.content || !(attachment.content instanceof Blob)) {
            console.warn('Skipping invalid attachment for event', item.key);
            continue;
          }

          if (this.commentChangeTypes.has(item.type)) {
            await this.commentControllerService
              .uploadAttachment(workOrderId, commentId, attachment.content)
              .toPromise();
            continue;
          }

          const payload: UploadChangeAttachmentRequest = { file: attachment.content };
          await this.changeLogController.uploadChangeAttachment(item.key, attachment.id, payload).toPromise();
        }
      } catch (e) {
        console.warn('Attachment upload failed for event', item.key, e);
        return { success: false, retryable: this.isRetryableSyncError(e) };
      }
    }

    return { success: true, retryable: true };
  }

  private isRetryableSyncError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return true;
    }

    const status = error.status;
    if (status === 404) {
      return false;
    }

    return true;
  }

  private async verifyCommentChange(type: string, workOrderId: string, commentId: string): Promise<boolean> {
    if (!workOrderId || !commentId) {
      return true;
    }

    if (type !== 'WO_ADD_COMMENT' && type !== 'WO_UPDATE_COMMENT' && type !== 'WO_DELETE_COMMENT') {
      return true;
    }

    for (let attempt = 1; attempt <= this.verificationAttempts; attempt++) {
      try {
        const comments =
          (await this.commentControllerService.getComments(workOrderId).toPromise()) ?? ([] as CommentDto[]);
        const exists = this.commentExists(comments, commentId);

        if (type === 'WO_DELETE_COMMENT' ? !exists : exists) {
          return true;
        }
      } catch {
        // Keep retrying while online; transient API errors can happen during reconnect.
      }

      if (!this.canRetryImmediately() || attempt === this.verificationAttempts) {
        break;
      }

      await this.delay(this.verificationRetryDelayMs);
    }

    return false;
  }

  private commentExists(comments: CommentDto[], commentId: string): boolean {
    return comments.some(
      (comment) =>
        comment.id === commentId ||
        (Array.isArray(comment.replies) && comment.replies.some((reply) => reply.id === commentId)),
    );
  }

  private async delay(ms: number): Promise<void> {
    await new Promise<void>((resolve) => setTimeout(resolve, ms));
  }

  private canRetryImmediately(): boolean {
    return navigator.onLine && this.loaderService.isBackendReachable;
  }

  private scheduleDelayedSync(): void {
    if (this.syncScheduled) {
      return;
    }

    this.syncScheduled = true;
    setTimeout(async () => {
      this.syncScheduled = false;
      if (!navigator.onLine) {
        return;
      }
      await this.syncChannel.postMessage({ type: 'sync' });
    }, this.delayedResyncMs);
  }
}
